import { isReservedProductBarcode, parseBahtToSatang } from '@pos/types';

import type { ProductUnit } from '../../../types/domain';

// xlsx is heavy (~hundreds of KB); load it on demand so it stays out of the
// main bundle (mirrors lib/export.ts). ProductImportView is eagerly routed.
const getXLSX = () => import('xlsx');

export type ProductImportRawRow = Record<string, unknown>;

export interface ProductImportIssue {
  code:
    | 'missing_required'
    | 'invalid_number'
    | 'negative_number'
    | 'duplicate_barcode'
    | 'reserved_barcode'
    | 'duplicate_unit_conflict'
    | 'same_code_different_name'
    | 'similar_name'
    | 'generated_code'
    | 'default_category'
    | 'multiple_stock_rows'
    | 'opening_stock_cost_required';
  row?: number;
  message: string;
  productKey?: string;
}

export interface ProductImportUnit extends ProductUnit {
  openingStockQty: number;
  openingStockBaseQty: number;
}

export interface ProductImportProduct {
  importKey: string;
  generatedCode: boolean;
  id: string;
  name: string;
  categoryName: string;
  baseUnit: string;
  active: boolean;
  minStock: number;
  cost: number;
  units: ProductImportUnit[];
  openingStockBaseQty: number;
  openingStockSpecified?: boolean;
}

export interface ProductImportPreview {
  products: ProductImportProduct[];
  errors: ProductImportIssue[];
  warnings: ProductImportIssue[];
  summary: {
    productCount: number;
    unitCount: number;
    barcodeCount: number;
    openingStockProductCount: number;
  };
}

const REQUIRED_HEADERS = [
  'ชื่อสินค้า',
  'หน่วยขาย',
  'จำนวนต่อหน่วยฐาน',
  'ราคาขาย',
] as const;

const text = (value: unknown): string => String(value ?? '').trim();
const normalize = (value: string): string =>
  value.trim().normalize('NFKC').replace(/\s+/g, ' ').toLowerCase();
const compactName = (value: string): string =>
  normalize(value).replace(/[^\p{L}\p{N}]/gu, '');
const numberValue = (value: unknown): number => {
  if (typeof value === 'number') return value;
  const parsed = Number(
    String(value ?? '')
      .replace(/,/g, '')
      .trim(),
  );
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};
const TRUE_VALUES = new Set(['yes', 'true', '1', 'ใช่', 'เปิด']);
const FALSE_VALUES = new Set(['no', 'false', '0', 'ไม่ใช่', 'ปิด']);
const MONEY_REGEX = /^\s*[+-]?(?:\d{1,3}(?:,\d{3})*|\d+)(?:\.\d+)?\s*$/;
const parseMoneyToSatang = (value: unknown): number => {
  const raw = text(value);
  if (raw === '') return 0;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return Number.NaN;
    return parseBahtToSatang(raw);
  }

  if (!MONEY_REGEX.test(raw)) return Number.NaN;
  const normalized = raw.replace(/,/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parseBahtToSatang(normalized) : Number.NaN;
};
const isActive = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  const normalized = normalize(text(value));
  if (typeof value === 'number') {
    return !FALSE_VALUES.has(String(value));
  }
  if (TRUE_VALUES.has(normalized)) {
    return true;
  }
  if (FALSE_VALUES.has(normalized)) {
    return false;
  }
  return true;
};
const generatedId = (key: string, index: number): string =>
  `import-${index + 1}-${
    normalize(key)
      .replace(/[^a-z0-9ก-๙]+/gi, '-')
      .replace(/^-|-$/g, '') || 'product'
  }`;

export function parseProductImportRows(
  rows: ProductImportRawRow[],
): ProductImportPreview {
  const errors: ProductImportIssue[] = [];
  const warnings: ProductImportIssue[] = [];
  const groups = new Map<string, ProductImportProduct>();
  const barcodeOwner = new Map<string, string>();

  rows.forEach((row, index) => {
    const rowNo = index + 2;
    let missingRequired = false;
    for (const header of REQUIRED_HEADERS) {
      if (!text(row[header])) {
        missingRequired = true;
        errors.push({
          code: 'missing_required',
          row: rowNo,
          message: `แถว ${rowNo}: ต้องกรอก ${header}`,
        });
      }
    }

    const name = text(row['ชื่อสินค้า']);
    const code = text(row['รหัสสินค้า']);
    const unitName = text(row['หน่วยขาย']);
    const baseUnit = text(row['หน่วยฐาน']) || unitName;
    const categoryName = text(row['หมวดหมู่']) || 'ทั่วไป';
    const key = code || name;
    const normalizedKey = normalize(key);
    const factor = numberValue(row['จำนวนต่อหน่วยฐาน']);
    const price = parseMoneyToSatang(row['ราคาขาย']);
    const hasOpeningStockQty = text(row['สต๊อกปัจจุบัน']) !== '';
    const stockQtyRaw = hasOpeningStockQty
      ? numberValue(row['สต๊อกปัจจุบัน'])
      : 0;
    const cost = parseMoneyToSatang(row['ต้นทุน']);
    const minStock = text(row['สต๊อกขั้นต่ำ'])
      ? numberValue(row['สต๊อกขั้นต่ำ'])
      : 0;

    let hasInvalidNumericValue = false;
    for (const [label, value] of [
      ['จำนวนต่อหน่วยฐาน', factor],
      ['ราคาขาย', price],
      ['สต๊อกปัจจุบัน', stockQtyRaw],
      ['ต้นทุน', cost],
      ['สต๊อกขั้นต่ำ', minStock],
    ] as const) {
      if (!Number.isFinite(value)) {
        hasInvalidNumericValue = true;
        errors.push({
          code: 'invalid_number',
          row: rowNo,
          message: `แถว ${rowNo}: ${label} ต้องเป็นตัวเลข`,
        });
      } else if (value < 0) {
        hasInvalidNumericValue = true;
        errors.push({
          code: 'negative_number',
          row: rowNo,
          message: `แถว ${rowNo}: ${label} ห้ามติดลบ`,
        });
      }
    }

    if (missingRequired || !key || !unitName || hasInvalidNumericValue) {
      return;
    }

    if (stockQtyRaw > 0 && cost <= 0) {
      errors.push({
        code: 'opening_stock_cost_required',
        row: rowNo,
        productKey: normalizedKey,
        message: `แถว ${rowNo}: ต้องกรอกต้นทุนมากกว่า 0 เมื่อมีสต๊อกตั้งต้น`,
      });
      return;
    }

    if (!code) {
      warnings.push({
        code: 'generated_code',
        row: rowNo,
        productKey: normalizedKey,
        message: `แถว ${rowNo}: ไม่ได้กรอกรหัสสินค้า ระบบจะสร้างรหัสภายในให้`,
      });
    }
    if (!text(row['หมวดหมู่'])) {
      warnings.push({
        code: 'default_category',
        row: rowNo,
        productKey: normalizedKey,
        message: `แถว ${rowNo}: ไม่ได้กรอกหมวดหมู่ ระบบจะใช้ "ทั่วไป"`,
      });
    }

    let product = groups.get(normalizedKey);
    if (!product) {
      product = {
        importKey: key,
        generatedCode: !code,
        id: code || generatedId(key, groups.size),
        name,
        categoryName,
        baseUnit,
        active: isActive(row['เปิดขาย']),
        minStock,
        cost,
        units: [],
        openingStockBaseQty: 0,
        openingStockSpecified: hasOpeningStockQty,
      };
      groups.set(normalizedKey, product);
    } else if (normalize(product.name) !== normalize(name)) {
      warnings.push({
        code: 'same_code_different_name',
        row: rowNo,
        productKey: normalizedKey,
        message: `รหัสสินค้า ${product.importKey} มีชื่อสินค้าไม่ตรงกัน: "${product.name}" และ "${name}"`,
      });
    }
    if (hasOpeningStockQty) {
      product.openingStockSpecified = true;
      if (cost > 0) {
        product.cost = cost;
      }
    }

    const existingUnit = product.units.find(
      (unit) => normalize(unit.unit) === normalize(unitName),
    );
    if (
      existingUnit &&
      (existingUnit.factor !== factor || existingUnit.price !== price)
    ) {
      errors.push({
        code: 'duplicate_unit_conflict',
        row: rowNo,
        productKey: normalizedKey,
        message: `สินค้า ${product.name}: หน่วย ${unitName} ซ้ำแต่ราคา/จำนวนต่อหน่วยฐานไม่ตรงกัน`,
      });
      return;
    }

    const barcode = text(row['บาร์โค้ด']);
    if (barcode) {
      if (isReservedProductBarcode(barcode)) {
        errors.push({
          code: 'reserved_barcode',
          row: rowNo,
          productKey: normalizedKey,
          message: `แถว ${rowNo}: บาร์โค้ด ${barcode} ใช้ไม่ได้ เนื่องจาก QTY ถูกสงวนไว้สำหรับคำสั่งจำนวนของ POS`,
        });
      }
      const owner = barcodeOwner.get(barcode);
      if (owner) {
        errors.push({
          code: 'duplicate_barcode',
          row: rowNo,
          productKey: normalizedKey,
          message: `บาร์โค้ด ${barcode} ซ้ำ`,
        });
      }
      barcodeOwner.set(barcode, normalizedKey);
    }

    // Only count opening stock for a newly-seen unit. An exact-duplicate row
    // (same unit, matching factor/price) reaches here without adding a unit, so
    // accumulating here too would double-count its stock silently.
    if (!existingUnit) {
      const openingStockBaseQty = stockQtyRaw * factor;
      product.units.push({
        id: `${product.id}:unit:${product.units.length + 1}`,
        code: unitName,
        unit: unitName,
        factor,
        price,
        barcode,
        barcodeId: barcode
          ? `${product.id}:unit:${product.units.length + 1}:barcode`
          : undefined,
        openingStockQty: stockQtyRaw,
        openingStockBaseQty,
      });
      product.openingStockBaseQty += openingStockBaseQty;
    }
  });

  const products = Array.from(groups.values());
  for (const product of products) {
    if (product.units.filter((unit) => unit.openingStockQty > 0).length > 1) {
      warnings.push({
        code: 'multiple_stock_rows',
        productKey: normalize(product.importKey),
        message: `${product.name}: มีการกรอกสต๊อกหลายหน่วย ระบบจะรวมเป็น ${product.openingStockBaseQty} ${product.baseUnit}`,
      });
    }
  }

  // Group by compacted name so distinct-key collisions are found in O(n) rather
  // than a pairwise O(n^2) scan (the predicate is exact equality on `compact`).
  const byCompact = new Map<string, { key: string; name: string }[]>();
  for (const product of products) {
    const compact = compactName(product.name);
    if (!compact) continue;
    const bucket = byCompact.get(compact);
    const entry = { key: normalize(product.importKey), name: product.name };
    if (bucket) bucket.push(entry);
    else byCompact.set(compact, [entry]);
  }
  for (const bucket of byCompact.values()) {
    if (bucket.length < 2) continue;
    for (let left = 0; left < bucket.length; left += 1) {
      for (let right = left + 1; right < bucket.length; right += 1) {
        const a = bucket[left]!;
        const b = bucket[right]!;
        if (a.key !== b.key) {
          warnings.push({
            code: 'similar_name',
            message: `ชื่อสินค้า "${a.name}" และ "${b.name}" คล้ายกัน แต่ระบบจะไม่รวมให้อัตโนมัติ`,
          });
        }
      }
    }
  }

  return {
    products,
    errors,
    warnings,
    summary: {
      productCount: products.length,
      unitCount: products.reduce(
        (sum, product) => sum + product.units.length,
        0,
      ),
      barcodeCount: products.reduce(
        (sum, product) =>
          sum + product.units.filter((unit) => unit.barcode).length,
        0,
      ),
      openingStockProductCount: products.filter(
        (product) => product.openingStockBaseQty > 0,
      ).length,
    },
  };
}

export async function parseProductImportWorkbook(
  buffer: ArrayBuffer,
): Promise<ProductImportPreview> {
  const XLSX = await getXLSX();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets['สินค้า'];
  if (!sheet) {
    throw new Error(
      'ไม่พบชีต "สินค้า" ในไฟล์อัปโหลด กรุณาใช้เทมเพลตที่ถูกต้อง',
    );
  }
  const rows = XLSX.utils.sheet_to_json<ProductImportRawRow>(sheet, {
    defval: '',
  });
  return parseProductImportRows(rows);
}

export {
  buildProductImportTemplateWorkbook,
  downloadProductImportTemplate,
} from './productImportTemplate';
